'use client'; // Error boundaries must be Client Components

import { Column, Padded } from '@/components/layout/column';

import { CONSTANTS } from '../../constants';
import { Footer } from '@/components/layout/footer';
import { Header } from '@/components/header/header';
import { HeaderSection } from '@/components/header/header-section';
import { Main } from '@/components/layout/main';
import { sendErrorSms } from './server-actions';
import { useEffect } from 'react';

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    console.error(error);

    // ChunkLoadError is a normal side-effect of deploying a new build — old cached
    // pages reference chunk hashes that no longer exist. Reloading fetches fresh HTML
    // with correct chunk URLs, so the user recovers automatically. No SMS needed.
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      window.location.reload();
      return;
    }

    const errorMessage = error.message || error.toString() || '';
    
    // Suppress notifications for errors that are transient, user-related, or don't require developer intervention
    const shouldSuppress = 
      // 1. User validation / Auth lifecycle errors
      errorMessage.includes('Email missing') || 
      errorMessage.includes('User not found') ||
      errorMessage.includes('[auth][error]') ||
      errorMessage.includes('authjs.dev') ||
      
      // 2. Next.js navigation & deployment artifacts (e.g. submitting forms on old deployments)
      errorMessage.includes('NEXT_REDIRECT') ||
      errorMessage.includes('Failed to find Server Action') ||
      
      // 3. Transient network or database socket connection resets/timeouts
      errorMessage.includes('ECONNRESET') ||
      errorMessage.includes('ETIMEDOUT') ||
      errorMessage.includes('turso.io');

    if (!shouldSuppress) {
      sendErrorSms(error.digest || errorMessage || 'Unknown error');
    }
  }, [error]);

  return (
    <>
      <HeaderSection>
        <Header />
      </HeaderSection>
      <Main>
        <Column>
          <Padded className='flex flex-col justify-center items-center gap-4 min-h-[50vh]'>
            <div>
              A server error occurred. <br />
              digest: {error.digest}
            </div>
            <div>
              Please contact{' '}
              <a className='underline' href={`mailto:${CONSTANTS.EMAIL.SUPPORT}`}>
                {CONSTANTS.EMAIL.SUPPORT}
              </a>
            </div>
          </Padded>
        </Column>
      </Main>
      <Footer />
    </>
  );
}
